/**
 * phoenix-customizer-simple.js
 * Uploads customer graphics to Cloudinary at file-select time,
 * stores the returned CDN URL in a hidden input that submits as a
 * line item property (so it appears clickable in Shopify admin & Shipstation).
 */

const CLOUDINARY_CLOUD_NAME = 'dtnr0k2yp';
const CLOUDINARY_UPLOAD_PRESET = 'phoenix_putters';

async function uploadToCloudinary(file) {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
    fd.append('folder', 'phoenix-orders');

    const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`, {
        method: 'POST',
        body: fd
    });

    if (!res.ok) {
        const errBody = await res.text();
        throw new Error(`Image upload failed: ${errBody}`);
    }

    const data = await res.json();
    return data.secure_url;
}

const initPhoenixCustomizer = () => {
    const customizers = document.querySelectorAll('.phx-customizer:not([data-initialized])');
    
    customizers.forEach(customizer => {
        const toggleBtn = customizer.querySelector('.phx-toggle-btn');
        const content = customizer.querySelector('.phx-content-wrapper');

        if (!toggleBtn || !content) return;

        // Toggle Section
        toggleBtn.addEventListener('click', () => {
            const isOpen = content.classList.contains('open');
            content.classList.toggle('open');
            toggleBtn.classList.toggle('active');
            toggleBtn.querySelector('.phx-btn-text').textContent =
                isOpen ? 'Customize it' : 'Close Customizer';
        });

        // Character Counters
        customizer.querySelectorAll('.phx-text-input').forEach(input => {
            const wrapper = input.closest('.phx-input-wrapper');
            const charCount = wrapper?.querySelector('.phx-char-count-current');
            if (charCount) {
                input.addEventListener('input', () => {
                    charCount.textContent = input.value.length;
                    charCount.style.color = input.value.length >= 100 ? '#ff4444' : '';
                });
            }
        });

        // File Upload Zones — async upload to Cloudinary CDN
        const uploadZones = customizer.querySelectorAll('.phx-upload-zone');
        uploadZones.forEach(zone => {
            const step = zone.closest('.phx-step');
            const fileInput = zone.querySelector('input[type="file"]');
            const hiddenInputId = zone.dataset.uploadTarget;
            const hiddenInput = hiddenInputId ? document.getElementById(hiddenInputId) : null;
            const previewContainer = step?.querySelector('.phx-preview-container');
            const previewImg = previewContainer?.querySelector('.phx-preview-img');
            const removeBtn = previewContainer?.querySelector('.phx-remove-preview');
            const statusEl = zone.querySelector('.phx-upload-status');

            if (!fileInput) return;

            // Track upload state on the zone element so the form-submit guard can read it
            zone.dataset.uploadState = 'idle'; // idle | uploading | uploaded | failed

            async function handleFiles(files) {
                if (!files || !files[0]) return;
                const file = files[0];

                // Validate file size (5MB max)
                if (file.size > 5 * 1024 * 1024) {
                    alert('File must be under 5MB.');
                    return;
                }

                // Show local preview immediately
                const reader = new FileReader();
                reader.onload = (e) => {
                    if (previewImg) previewImg.src = e.target.result;
                    if (previewContainer) previewContainer.style.display = 'block';
                };
                reader.readAsDataURL(file);

                // Upload to Cloudinary so the order stores a clickable CDN URL
                zone.dataset.uploadState = 'uploading';
                if (hiddenInput) hiddenInput.value = '';
                if (statusEl) {
                    statusEl.textContent = 'Uploading...';
                    statusEl.style.color = '#888';
                }

                try {
                    const cdnUrl = await uploadToCloudinary(file);
                    if (hiddenInput) hiddenInput.value = cdnUrl;
                    zone.dataset.uploadState = 'uploaded';
                    if (statusEl) {
                        statusEl.textContent = '✓ Uploaded';
                        statusEl.style.color = '#4CAF50';
                    }
                } catch (err) {
                    console.error('Cloudinary upload failed:', err);
                    alert('Graphic upload failed. Please try again.');
                    if (hiddenInput) hiddenInput.value = '';
                    zone.dataset.uploadState = 'failed';
                    if (previewContainer) previewContainer.style.display = 'none';
                    if (previewImg) previewImg.src = '';
                    if (statusEl) {
                        statusEl.textContent = '✗ Upload failed';
                        statusEl.style.color = '#ff4444';
                    }
                }
            }

            // Click to browse
            zone.addEventListener('click', (e) => {
                if (e.target.closest('.phx-remove-preview')) return;
                fileInput.click();
            });

            fileInput.addEventListener('change', (e) => handleFiles(e.target.files));

            // Remove preview
            if (removeBtn) {
                removeBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    fileInput.value = '';
                    if (previewContainer) previewContainer.style.display = 'none';
                    if (previewImg) previewImg.src = '';
                    if (hiddenInput) hiddenInput.value = '';
                    zone.dataset.uploadState = 'idle';
                    if (statusEl) statusEl.textContent = '';
                });
            }

            // Drag and Drop
            ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(evt => {
                zone.addEventListener(evt, (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                }, false);
            });
            zone.addEventListener('dragover', () => zone.classList.add('dragover'));
            zone.addEventListener('dragleave', () => zone.classList.remove('dragover'));
            zone.addEventListener('drop', (e) => {
                zone.classList.remove('dragover');
                handleFiles(e.dataTransfer.files);
            });
        });

        // Robust safeguard: block product form submission if any upload is
        // pending or failed. Catches race conditions, network failures,
        // and stale-cache scenarios so a customer can never check out
        // with a graphic that didn't make it to the CDN.
        if (uploadZones.length > 0) {
            // Find every product form on the page that any of our hidden inputs targets
            const formIds = new Set();
            uploadZones.forEach(zone => {
                const hiddenInputId = zone.dataset.uploadTarget;
                const hiddenInput = hiddenInputId ? document.getElementById(hiddenInputId) : null;
                const formId = hiddenInput?.getAttribute('form');
                if (formId) formIds.add(formId);
            });

            formIds.forEach(formId => {
                const form = document.getElementById(formId);
                if (!form || form.dataset.phxGuardAttached) return;
                form.dataset.phxGuardAttached = 'true';

                form.addEventListener('submit', (e) => {
                    let pending = 0;
                    let failed = 0;
                    uploadZones.forEach(zone => {
                        const state = zone.dataset.uploadState;
                        if (state === 'uploading') pending++;
                        else if (state === 'failed') failed++;
                    });

                    if (pending > 0 || failed > 0) {
                        e.preventDefault();
                        e.stopImmediatePropagation();
                        if (pending > 0) {
                            alert('Please wait — your graphic is still uploading. Try again in a few seconds.');
                        } else {
                            alert('A graphic upload failed. Please remove it and try again before adding to cart.');
                        }
                        return false;
                    }
                }, true); // capture phase so we run before theme's own submit handlers
            });
        }

        // Font Selection Logic + live preview
        customizer.querySelectorAll('.phx-font-step').forEach(step => {
            const select = step.querySelector('.phx-font-select');
            const customFontWrapper = step.querySelector('.phx-custom-font-wrapper');
            const customFontInput = step.querySelector('.phx-custom-font-input');
            const preview = step.querySelector('.phx-font-preview');

            // The text input lives in the preceding .phx-step sibling
            let textInput = null;
            let prev = step.previousElementSibling;
            while (prev && !textInput) {
                textInput = prev.querySelector?.('.phx-text-input');
                if (!textInput) prev = prev.previousElementSibling;
            }

            if (!select) return;

            const placeholder = preview?.dataset.phxPreviewPlaceholder || 'Your text will look like this';

            const updatePreview = () => {
                if (!preview) return;
                const typed = textInput?.value?.trim() || '';
                const isOther = select.value === 'Other';
                const fontName = isOther
                    ? (customFontInput?.value?.trim() || '')
                    : select.value;
                preview.textContent = typed || placeholder;
                preview.classList.toggle('phx-font-preview--empty', !typed);
                if (fontName) {
                    preview.style.fontFamily = `"${fontName}", sans-serif`;
                } else {
                    preview.style.fontFamily = '';
                }
            };

            select.addEventListener('change', (e) => {
                if (customFontWrapper) {
                    customFontWrapper.style.display = e.target.value === 'Other' ? 'block' : 'none';
                }
                updatePreview();
            });
            textInput?.addEventListener('input', updatePreview);
            customFontInput?.addEventListener('input', updatePreview);
            updatePreview();
        });

        // ------------------------------------------------------------------
        // Customization opt-in / opt-out gate
        // A choice is mandatory: the product form cannot be submitted until
        // the customer explicitly picks one, so "no customization" on an
        // order is always deliberate and never an accidental skip.
        // ------------------------------------------------------------------
        const choiceFieldset = customizer.querySelector('[data-phx-choice]');
        const choiceInputs = customizer.querySelectorAll('.phx-choice-input');
        const choiceProperty = customizer.querySelector('.phx-choice-property');
        const choiceError = customizer.querySelector('.phx-choice-error');

        const CHOICE_VALUES = {
            yes: 'Customized — see details below',
            no: 'No Customization Needed'
        };

        if (choiceInputs.length && choiceProperty) {
            const panelFields = () => customizer.querySelectorAll(
                '.phx-content-wrapper input, .phx-content-wrapper select, .phx-content-wrapper textarea'
            );

            const setPanelFieldsDisabled = (disabled) => {
                panelFields().forEach(field => { field.disabled = disabled; });
            };

            const resetCustomFields = () => {
                customizer.querySelectorAll('.phx-text-input').forEach(input => {
                    input.value = '';
                    const counter = input.closest('.phx-input-wrapper')?.querySelector('.phx-char-count-current');
                    if (counter) {
                        counter.textContent = '0';
                        counter.style.color = '';
                    }
                });

                customizer.querySelectorAll('.phx-custom-font-wrapper').forEach(wrapper => {
                    wrapper.style.display = 'none';
                });

                customizer.querySelectorAll('.phx-font-select').forEach(select => {
                    select.selectedIndex = 0;
                });

                customizer.querySelectorAll('.phx-upload-zone').forEach(zone => {
                    const step = zone.closest('.phx-step');
                    const fileInput = zone.querySelector('input[type="file"]');
                    const statusEl = zone.querySelector('.phx-upload-status');
                    const hiddenInputId = zone.dataset.uploadTarget;
                    const hiddenInput = hiddenInputId ? document.getElementById(hiddenInputId) : null;
                    const previewContainer = step?.querySelector('.phx-preview-container');
                    const previewImg = previewContainer?.querySelector('.phx-preview-img');

                    if (fileInput) fileInput.value = '';
                    if (hiddenInput) hiddenInput.value = '';
                    if (previewContainer) previewContainer.style.display = 'none';
                    if (previewImg) previewImg.src = '';
                    if (statusEl) statusEl.textContent = '';
                    zone.dataset.uploadState = 'idle';
                });

                customizer.querySelectorAll('.phx-font-preview').forEach(preview => {
                    const placeholder = preview.dataset.phxPreviewPlaceholder || 'Your text will look like this';
                    preview.textContent = placeholder;
                    preview.classList.add('phx-font-preview--empty');
                    preview.style.fontFamily = '';
                });
            };

            const setPanelOpen = (open) => {
                content.classList.toggle('open', open);
                toggleBtn.classList.toggle('active', open);
                const label = toggleBtn.querySelector('.phx-btn-text');
                if (label) label.textContent = open ? 'Close Customizer' : 'Customize it';
            };

            const applyChoice = (choice) => {
                const optedOut = choice === 'no';

                customizer.classList.toggle('phx-opted-out', optedOut);
                if (choiceError) choiceError.hidden = true;
                if (choiceFieldset) choiceFieldset.classList.remove('phx-choice--invalid');

                if (optedOut) {
                    // Clear anything already entered, then disable so no stray
                    // text or leftover upload can ride along on the order.
                    resetCustomFields();
                    setPanelOpen(false);
                    setPanelFieldsDisabled(true);
                } else {
                    setPanelFieldsDisabled(false);
                    setPanelOpen(true);
                }

                choiceProperty.value = CHOICE_VALUES[choice] || '';
                choiceProperty.disabled = !choice;
            };

            choiceInputs.forEach(input => {
                input.addEventListener('change', () => {
                    if (input.checked) applyChoice(input.dataset.phxChoiceValue);
                });
            });

            // Opening the panel is itself an opt-in
            toggleBtn.addEventListener('click', () => {
                const alreadyChosen = Array.from(choiceInputs).some(input => input.checked);
                if (alreadyChosen) return;
                const yesInput = customizer.querySelector('.phx-choice-input[data-phx-choice-value="yes"]');
                if (yesInput) {
                    yesInput.checked = true;
                    applyChoice('yes');
                }
            });

            // Honour browser-restored radio state on back/refresh
            const preselected = Array.from(choiceInputs).find(input => input.checked);
            if (preselected) {
                applyChoice(preselected.dataset.phxChoiceValue);
            } else {
                setPanelFieldsDisabled(true);
                choiceProperty.disabled = true;
            }

            // Block add-to-cart until a choice is made
            const choiceFormId = choiceProperty.getAttribute('form');
            const choiceForm = choiceFormId ? document.getElementById(choiceFormId) : null;

            if (choiceForm && !choiceForm.dataset.phxChoiceGuardAttached) {
                choiceForm.dataset.phxChoiceGuardAttached = 'true';

                choiceForm.addEventListener('submit', (e) => {
                    const chosen = customizer.querySelector('.phx-choice-input:checked');
                    if (!chosen) {
                        e.preventDefault();
                        e.stopImmediatePropagation();
                        if (choiceError) choiceError.hidden = false;
                        if (choiceFieldset) {
                            choiceFieldset.classList.add('phx-choice--invalid');
                            choiceFieldset.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                        return false;
                    }
                }, true); // capture phase so we run before the theme's own handlers
            }
        }

        // Mark as initialized
        customizer.setAttribute('data-initialized', 'true');
    });
};

// Listen for standard page load
document.addEventListener('DOMContentLoaded', initPhoenixCustomizer);

// Listen for Shopify Editor reloads
document.addEventListener('shopify:section:load', () => initPhoenixCustomizer());

// Fallback: If DOMContentLoaded already fired
if (document.readyState === 'complete' || document.readyState === 'interactive') {
    initPhoenixCustomizer();
}
